import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, MapPin, Clock, Facebook, Instagram, Youtube, Twitter } from 'lucide-react';
import config from '@/config';

export default function Footer() {
  const { t, i18n } = useTranslation();
  const isHindi = i18n.language === 'hi';

  const socialLinks = [
    { url: config.social.facebook, icon: Facebook, label: 'Facebook' },
    { url: config.social.instagram, icon: Instagram, label: 'Instagram' },
    { url: config.social.youtube, icon: Youtube, label: 'YouTube' },
    { url: config.social.twitter, icon: Twitter, label: 'Twitter' },
  ].filter((s) => s.url);

  return (
    <footer className="mt-auto bg-gray-900 text-gray-300">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {/* Quick Links */}
          <div>
            <h3 className="mb-4 text-lg font-semibold text-white">
              {t('footer.quickLinks')}
            </h3>
            <nav className="flex flex-col gap-2">
              <Link to="/" className="hover:text-white">{t('nav.home')}</Link>
              <Link to="/products" className="hover:text-white">{t('nav.products')}</Link>
              <Link to="/services" className="hover:text-white">{t('nav.services')}</Link>
              <Link to="/about" className="hover:text-white">{t('nav.about')}</Link>
              <Link to="/contact" className="hover:text-white">{t('nav.contact')}</Link>
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="mb-4 text-lg font-semibold text-white">
              {t('footer.contactInfo')}
            </h3>
            <div className="flex flex-col gap-3">
              <a href={`tel:${config.contact.phone}`} className="flex items-center gap-2 hover:text-white">
                <Phone size={16} />
                <span>{config.contact.phone}</span>
              </a>
              <a href={`mailto:${config.contact.email}`} className="flex items-center gap-2 hover:text-white">
                <Mail size={16} />
                <span>{config.contact.email}</span>
              </a>
              <div className="flex items-start gap-2">
                <MapPin size={16} className="mt-0.5 shrink-0" />
                <span>{isHindi ? config.contact.addressHi : config.contact.address}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock size={16} />
                <span>{isHindi ? config.business.hoursHi : config.business.hours}</span>
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div>
            {socialLinks.length > 0 && (
              <>
                <h3 className="mb-4 text-lg font-semibold text-white">
                  {t('footer.followUs')}
                </h3>
                <div className="flex gap-3">
                  {socialLinks.map((social) => (
                    <a
                      key={social.label}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-800 transition-colors hover:bg-primary"
                      aria-label={social.label}
                    >
                      <social.icon size={18} />
                    </a>
                  ))}
                </div>
              </>
            )}

            {/* Brand */}
            <div className="mt-6">
              <p className="text-xl font-bold text-white">{config.brand.name}</p>
              <p className="mt-1 text-sm">
                {isHindi ? config.brand.taglineHi : config.brand.tagline}
              </p>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-8 border-t border-gray-800 pt-6 text-center text-sm">
          <p>
            &copy; {new Date().getFullYear()} {config.brand.name}. {t('footer.rights')}
          </p>
        </div>
      </div>
    </footer>
  );
}
